/**
 * @file: PlanDetailItemBody.tsx
 *
 * One maintenance-plan detail rendered in the plans timeline. Same body as the
 * feed reminder (`MaintenancePlanRemindBody`) minus the order block — that
 * lives once in the group header. Reuses the shared plan-detail card, the
 * three-dots actions menu, and the convert/view-lead affordance; the row
 * already carries its lead coverage, so the affordance needs no extra lookup.
 */

import { Typography } from '@euler/app/components/typography';
import { LeadAffordanceButton } from '@euler/app/flows/feed/components/message-body/common/LeadAffordanceButton';
import {
  PlanDetailActionsButton,
  PlanDetailEditPatch,
} from '@euler/app/flows/feed/components/message-body/common/PlanDetailActionsButton';
import { PlanDetailCard } from '@euler/app/flows/feed/components/message-body/common/PlanDetailCard';
import {
  abnormalLevelInfo,
  detailTypeName,
} from '@euler/app/flows/feed/components/message-body/common/labels';
import {
  Chip,
  LabelRow,
  TitleText,
  labelColor,
  valueColor,
} from '@euler/app/flows/feed/components/message-body/common/primitives';
import { Img } from '@euler/components/image/AdvancedImage';
import {
  getDefectiveLevelColor,
  getDefectiveLevelFrom,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MaintenanceServicePlanDetailType } from '@euler/model/enum/MaintenanceServicePlanDetailType';
import { MaintenancePlanDetailListItem } from '@euler/model/task-detail';
import { formatDateTime } from '@euler/utils/formatters';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import parseISO from 'date-fns/parseISO';
import startOfToday from 'date-fns/startOfToday';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { useTheme } from 'styled-components';
import { AlertStatusInfo } from './MaintenancePlanCell';

export const PlanDetailItemBody: FC<{
  item: MaintenancePlanDetailListItem;
  editable: boolean;
  onEdited: (patch: PlanDetailEditPatch) => void;
  onRemoved: () => void;
  onAlertChanged: (settings: {
    isAlertEnabled: boolean;
    isAlertEnabledForCustomer: boolean;
  }) => void;
}> = memo(({ item, editable, onEdited, onRemoved, onAlertChanged }) => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  // Right-edge severity bar — the defective-level color, like the old cell.
  const levelColor = getDefectiveLevelColor(theme, getDefectiveLevelFrom(item));
  const subjectName = item.customAlertSubjectName ?? item.name;
  // Re-derive the due badge against today (the stored value goes stale).
  const dateStr = String(item.nextServiceDate).slice(0, 10);
  const days = differenceInCalendarDays(parseISO(dateStr), startOfToday());
  const dueColor = days < 0 ? '#ff3b30' : days <= 1 ? '#ff9500' : '#1f80ff';
  const dueText =
    days > 0
      ? `${days} 天后到期`
      : days === 0
      ? '今日到期'
      : `已过期 ${Math.abs(days)} 天`;
  const ab = abnormalLevelInfo(item.abnormalLevel);
  const reasonLabel =
    (item.type === MaintenanceServicePlanDetailType.FaultLikeSubject
      ? '未解决'
      : '遗留') + '原因';
  const signatureImgUrl = item.customerSignatureImgUrl;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'stretch' }}>
      <View style={{ flex: 1 }}>
        <View
          css={`
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
          `}
        >
          <TitleText>{subjectName}</TitleText>
          <View style={{ marginLeft: rpx(8) }}>
            <Chip text={detailTypeName(item.type)} color="#6b7280" />
          </View>
          <View style={{ marginLeft: rpx(6) }}>
            <Chip text={ab.name} color={ab.color} />
          </View>
        </View>
        <PlanDetailCard
          detail={item}
          nextServiceTrailing={<Chip text={dueText} color={dueColor} />}
        />
        {item.unsolvedReason || signatureImgUrl ? (
          <View
            css={`
              flex-direction: row;
              align-items: center;
              flex-wrap: wrap;
              margin-top: ${rpx(4)}px;
            `}
          >
            {item.unsolvedReason ? (
              <>
                <Typography.Text
                  size={rpx(12)}
                  color={labelColor}
                  style={{ minWidth: rpx(56) }}
                >
                  {reasonLabel}
                </Typography.Text>
                <Typography.Text size={rpx(12)} color={valueColor}>
                  {item.unsolvedReason}
                </Typography.Text>
              </>
            ) : null}
            {signatureImgUrl ? (
              <View style={{ marginLeft: item.unsolvedReason ? rpx(16) : 0 }}>
                <Img
                  uri={signatureImgUrl}
                  width={rpx(60)}
                  height={rpx(24)}
                  resizeMode="contain"
                />
              </View>
            ) : null}
          </View>
        ) : null}
        {item.isAlertEnabled && item.nextAlertTime ? (
          <LabelRow
            label="下次提醒"
            value={formatDateTime(item.nextAlertTime)}
          />
        ) : null}
        {item.isAlertEnabledForCustomer && item.nextAlertTimeForCustomer ? (
          <LabelRow
            label="客户提醒"
            value={formatDateTime(item.nextAlertTimeForCustomer)}
          />
        ) : null}
        <View
          css={`
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
          `}
        >
          <LeadAffordanceButton
            planDetailId={item.id}
            variant="outline"
            initialCoverage={{
              coverage: item.leadCoverage,
              leadId: item.leadId,
              leadStatus: item.leadStatus,
            }}
          />
        </View>
      </View>
      {/* Right gutter — three-dots on top, alert bells below. A sibling of the
          content column so it never adds vertical gaps. One bell when store +
          customer match, two labeled bells when they differ. */}
      <View style={{ alignItems: 'flex-end', marginLeft: rpx(10) }}>
        {editable ? (
          <PlanDetailActionsButton
            planNo={item.planNo}
            planDetailId={item.id}
            detail={item}
            onEdited={onEdited}
            onRemoved={onRemoved}
            onAlertChanged={onAlertChanged}
          />
        ) : null}
        <View style={{ marginTop: editable ? rpx(8) : 0 }}>
          {item.isAlertEnabled === item.isAlertEnabledForCustomer ? (
            <AlertStatusInfo enabled={item.isAlertEnabled} iconSize={rpx(24)} />
          ) : (
            <View style={{ gap: rpx(4) }}>
              <AlertStatusInfo enabled={item.isAlertEnabled} label="门店" />
              <AlertStatusInfo
                enabled={item.isAlertEnabledForCustomer}
                label="客户"
              />
            </View>
          )}
        </View>
      </View>
      <View
        style={{
          width: rpx(4),
          borderRadius: rpx(2),
          backgroundColor: levelColor,
          marginLeft: rpx(10),
          // Reach the card's right edge (cancel TimelineItem's right padding).
          marginRight: -rpx(16),
        }}
      />
    </View>
  );
});
